import * as THREE from 'three'

export class Section {
  constructor(scene, zPosition, sectionIndex = 0) {
    this.scene = scene
    this.zPosition = zPosition
    this.sectionIndex = sectionIndex
    this.isActive = false
    this.objects = []
    this.group = null
    
    // Background plane
    this.backgroundPlane = null
    this.bgMaterial = null
    this.fadeRange = 12 // Start fading 12 units away
    this.bgZOffset = sectionIndex * 1 // 1 unit apart to prevent z-fighting
    this.cameraFollowDistance = 10 // Distance behind camera
  }
  
  // Override in subclasses - create all 3D content here
  build(camera, renderer) {
    // Create a group for this section
    this.group = new THREE.Group()
    this.group.position.z = this.zPosition
    this.scene.add(this.group)
  }
  
  // Create a colored background plane for this section
  createBackground(color, opacity = 0.3) {
    // Calculate plane size based on viewport + parallax margin, locked to 900:1440 aspect ratio
    const viewportAspect = window.innerWidth / window.innerHeight
    const targetAspect = 1440 / 900
    const baseWidth = (20 * viewportAspect + 6) * 0.9
    const baseHeight = (20 + 6) * 0.9
    let width, height
    if (baseWidth / baseHeight > targetAspect) {
      width = baseWidth
      height = width / targetAspect
    } else {
      height = baseHeight
      width = height * targetAspect
    }
    
    const geometry = new THREE.PlaneGeometry(width, height)
    this.bgMaterial = new THREE.MeshBasicMaterial({
      color: color,
      transparent: true,
      opacity: 0, // Start invisible
      depthWrite: false,
      side: THREE.DoubleSide
    })
    
    this.backgroundPlane = new THREE.Mesh(geometry, this.bgMaterial)
    // Add directly to scene (not group) so we can control world position independently
    this.scene.add(this.backgroundPlane)
    
    // Store target color for potential texture replacement later
    this.bgColor = color
  }
  
  // Calculate section visibility opacity based on camera distance from section target.
  // Smooth fade for seamless crossfades between sections.
  updateSectionVisibility(cameraZ) {
    const cameraTarget = this.zPosition + 5
    const dist = Math.abs(cameraZ - cameraTarget)
    const fadeRange = 15
    let opacity = 1 - dist / fadeRange
    return Math.max(0, Math.min(1, opacity))
  }
  
  // Update background position and visibility based on camera
  updateBackgroundOpacity(cameraZ) {
    if (!this.backgroundPlane || !this.bgMaterial) return
    
    // Update plane position to follow camera (with unique z-offset to prevent z-fighting)
    const targetZ = cameraZ - this.cameraFollowDistance - this.bgZOffset
    this.backgroundPlane.position.z = targetZ
    
    // Smooth visibility fade based on distance from section's camera target
    const opacity = this.updateSectionVisibility(cameraZ)
    
    this.backgroundPlane.visible = opacity > 0.01
    this.bgMaterial.opacity = opacity
  }
  
  // Recalculate background plane geometry on resize (e.g. mobile orientation change)
  onResize() {
    if (!this.backgroundPlane) return
    
    const viewportAspect = window.innerWidth / window.innerHeight
    const targetAspect = 1440 / 900
    const baseWidth = (20 * viewportAspect + 6) * 0.9
    const baseHeight = (20 + 6) * 0.9
    let width, height
    if (baseWidth / baseHeight > targetAspect) {
      width = baseWidth
      height = width / targetAspect
    } else {
      height = baseHeight
      width = height * targetAspect
    }
    
    this.backgroundPlane.geometry.dispose()
    this.backgroundPlane.geometry = new THREE.PlaneGeometry(width, height)
  }
  
  // Called when entering this section
  onEnter() {
    this.isActive = true
    console.log(`Entered section at z=${this.zPosition}`)
  }
  
  // Called when leaving this section
  onExit() {
    this.isActive = false
    console.log(`Exited section at z=${this.zPosition}`)
  }
  
  // Called every frame when active
  update(delta, scrollProgress) {
    // Override in subclasses for animations
  }
  
  // Add object to this section
  add(object) {
    if (this.group) {
      this.group.add(object)
      this.objects.push(object)
    }
  }
  
  // Cleanup
  destroy() {
    this.objects.forEach(obj => {
      if (obj.geometry) obj.geometry.dispose()
      if (obj.material) {
        if (Array.isArray(obj.material)) {
          obj.material.forEach(m => m.dispose())
        } else {
          obj.material.dispose()
        }
      }
      if (this.group) this.group.remove(obj)
    })
    
    if (this.group) {
      this.scene.remove(this.group)
    }
    
    this.objects = []
  }
}
